/**
 * Spec 052, step 6: the words and rules of the clients' orders, kept apart from the screens so a
 * test reads them. The funnel moves as the server allows (packages/core/services/client_orders):
 * forward with skips, back only «Сдача → В работе» and «Потерян → Обсуждение», to «Потерян»
 * from any open stage with a reason; «Закрыт» moves nowhere.
 */

export type Stage =
  | "lead"
  | "discussion"
  | "proposal"
  | "contract"
  | "in_work"
  | "delivery"
  | "support"
  | "closed"
  | "lost";

/** The funnel in its order; «Потерян» stands aside. */
export const FUNNEL: Stage[] = ["lead", "discussion", "proposal", "contract", "in_work", "delivery", "support", "closed"];
/** The board's columns: the open stages; «Закрыт» and «Потерян» are behind a switch. */
export const OPEN_STAGES: Stage[] = FUNNEL.slice(0, -1);
export const FINISHED: Stage[] = ["closed", "lost"];

export const STAGE_LABEL: Record<Stage, string> = {
  lead: "Лид",
  discussion: "Обсуждение",
  proposal: "КП",
  contract: "Договор",
  in_work: "В работе",
  delivery: "Сдача",
  support: "Поддержка",
  closed: "Закрыт",
  lost: "Потерян",
};

/** Whether the server takes this move; a drop the board refuses never reaches it. */
export function canMove(from: Stage, to: Stage): boolean {
  if (from === to || from === "closed") return false;
  if (to === "lost") return from !== "lost";
  if (from === "lost") return to === "discussion";
  if (from === "delivery" && to === "in_work") return true;
  return FUNNEL.indexOf(to) > FUNNEL.indexOf(from);
}

/** Where an order may go from here, in the funnel's order. */
export function movesFrom(from: Stage): Stage[] {
  return [...FUNNEL, "lost" as Stage].filter((to) => canMove(from, to));
}

/** «150 000 ₽»; nothing when the sum is unknown or hidden by the right. */
export function rub(value: number | null | undefined): string {
  if (value == null) return "";
  return `${new Intl.NumberFormat("ru-RU").format(value).replace(/\s/g, " ")} ₽`;
}

// Spec 054: «addendum» — a supplementary agreement; its sum is optional and only kept.
export type DocumentKind = "brief" | "proposal" | "contract" | "license" | "act" | "addendum";
export const DOCUMENT_KINDS: DocumentKind[] = ["brief", "proposal", "contract", "license", "act", "addendum"];
export const DOCUMENT_LABEL: Record<DocumentKind, string> = {
  brief: "Бриф",
  proposal: "КП",
  contract: "Договор",
  license: "Лицензия",
  act: "Акт",
  addendum: "Допсоглашение",
};
/** Spec 061: «signed» — the client sent the contract back signed. */
export type DocumentState = "draft" | "approved" | "rejected" | "signed";
export const DOCUMENT_STATE: Record<DocumentState, string> = {
  draft: "черновик",
  approved: "утверждён",
  rejected: "отклонён",
  signed: "подписан клиентом",
};
/** What happened to a document, as the order's history says it (`to_value`). */
const DOCUMENT_EVENT: Record<string, string> = {
  approved: "утверждён",
  rejected: "отклонён",
  sent: "отправлен клиенту",
  signed: "подписан клиентом",
};

/** «contract v2» from the server → «Договор v2»; anything else stays as it is. */
export function documentWord(label: string): string {
  const [kind, revision] = label.split(" ");
  return kind in DOCUMENT_LABEL ? `${DOCUMENT_LABEL[kind as DocumentKind]} ${revision ?? ""}`.trim() : label;
}

/** «КП v2» — the server's label is «proposal v2». */
export function documentName(kind: DocumentKind, revision: number): string {
  return `${DOCUMENT_LABEL[kind]} v${revision}`;
}

interface MilestoneLike {
  position: number;
  title: string;
  planned_at: string | null;
  done_at: string | null;
}

/** The first milestone not done yet, by its place in the line. */
export function nextMilestone<T extends MilestoneLike>(milestones: T[]): T | null {
  return [...milestones].sort((a, b) => a.position - b.position).find((m) => !m.done_at) ?? null;
}

/** «12 нояб.» — a planned or done date of a milestone. */
export function day(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
}

interface EventLike {
  kind: string;
  from_value: string | null;
  to_value: string | null;
  note_md: string | null;
}

const stageWord = (value: string | null) => (value && value in STAGE_LABEL ? STAGE_LABEL[value as Stage] : value ?? "—");

/** One line of the order's history, in our words. */
export function eventLine(e: EventLike): string {
  switch (e.kind) {
    case "created":
      return "Заказ заведён";
    case "stage":
      return `Этап: ${stageWord(e.from_value)} → ${stageWord(e.to_value)}`;
    case "manager":
      return `Менеджер: ${e.from_value ?? "никто"} → ${e.to_value ?? "никто"}`;
    case "milestone":
      return `Веха: ${e.to_value ?? e.from_value ?? ""}`.trim();
    case "document":
      if (e.from_value && e.to_value && e.to_value in DOCUMENT_EVENT) return `${documentWord(e.from_value)}: ${DOCUMENT_EVENT[e.to_value]}`;
      return `Документ: ${documentWord(e.to_value ?? "")}`.trim();
    case "amount":
      return `Сумма: ${e.to_value ? rub(Number(e.to_value)) : "—"}`;
    case "payment":
      return `Оплата: ${e.to_value ? rub(Number(e.to_value)) : "—"}`;
    default:
      return e.kind;
  }
}

/** A sum typed by a person: digits and spaces, a minus for a correction; null if it is not one. */
export function parseRub(text: string): number | null {
  const clean = text.replace(/[\s₽]/g, "");
  if (!/^-?\d+$/.test(clean)) return null;
  return Number(clean);
}
